import React, { useState, useEffect } from "react";
import { StepperHeader } from "./StepperHeader";
import { StepNavigation } from "./StepNavigation";
import { InfoBox } from "newui/components/IconBox";
import type { StepProps } from "./Step";

interface StepContainerProps {
	children: React.ReactNode;
	highestStep?: number;
	initialStep?: number;
	isSubmitting?: boolean;
	onComplete?: () => void;
	onStepChange?: (step: number) => void;
}

export const StepContainer: React.FC<StepContainerProps> = ({
	children,
	highestStep: highestStepProp,
	onComplete,
	onStepChange,
	initialStep = 0,
	isSubmitting = false,
}) => {
	const [currentStep, setCurrentStep] = useState(initialStep);

	useEffect(() => {
		setCurrentStep(initialStep);
	}, [initialStep]);

	const steps = React.Children.toArray(children).filter(
		(child): child is React.ReactElement<StepProps> =>
			React.isValidElement(child)
	);

	const stepMeta = steps.map((step) => ({
		name: step.props.name,
		description: step.props.description,
		enabled: step.props.enabled,
		nextLabel: step.props.nextLabel,
		onBeforeNext: step.props.onBeforeNext,
	}));

	const highestStep = highestStepProp ?? currentStep;
	const activeStep = steps[currentStep];
	const currentStepEnabled = stepMeta[currentStep]?.enabled ?? false;
	const currentNextLabel = stepMeta[currentStep]?.nextLabel;
	const currentReadOnly = steps[currentStep]?.props?.readOnly ?? false;

	const changeStep = (step: number): void => {
		setCurrentStep(step);
		if (onStepChange) {
			onStepChange(step);
		}
	};

	const handleStepClick = (index: number): void => {
		if (index <= highestStep) {
			changeStep(index);
		}
	};

	const handleBack = (): void => {
		if (currentStep > 0) {
			changeStep(currentStep - 1);
		}
	};

	const handleNext = async (): Promise<void> => {
		if (currentStep < steps.length - 1 && currentStepEnabled) {
			const beforeNext = stepMeta[currentStep]?.onBeforeNext;
			if (beforeNext) {
				const proceed = await beforeNext();
				if (!proceed) {
					return;
				}
			}
			changeStep(currentStep + 1);
		}
	};

	const handleComplete = (): void => {
		if (onComplete) {
			onComplete();
		}
	};

	return (
		<div>
			<StepperHeader
				steps={stepMeta}
				currentStep={currentStep}
				highestStep={highestStep}
				onStepClick={handleStepClick}
			/>
			<div className="py-2">{activeStep}</div>
			<StepNavigation
				currentStep={currentStep}
				totalSteps={steps.length}
				currentStepEnabled={currentStepEnabled}
				isSubmitting={isSubmitting}
				nextLabel={currentNextLabel}
				readOnly={currentReadOnly}
				onBack={handleBack}
				onNext={handleNext}
				onComplete={handleComplete}
			/>
		</div>
	);
};
